// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * The Posegraf editor's state (frontend-aev .1 .5 .6 .7 .14): blocks,
 * suggestions, the rig selection, the gizmo delta and its previews, the
 * pose versions — and the `RigScene` that draws them. `GraphViewPage`
 * mounts it in Blokke mode and lays the panels out.
 *
 * Every write goes to ruxd (PR #19) and is followed by a
 * `posegraph.changed` event, which refetches the graph, the blocks, the
 * suggestions and the versions (`reloadAll`, also run after each write
 * here so a server without the event still refreshes).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';

import { api, ApiRequestError } from '../../api/client';
import type {
  BlockSuggestions,
  BlockTransformResult,
  PoseBlock,
  PoseGraph,
  PoseVersionDiff,
  PoseVersionList,
} from '../../api/types';
import { useCanEdit } from '../../app/CaseRoleContext';
import { useJobs } from '../../app/JobsContext';
import { useAsync } from '../../app/useAsync';
import {
  EMPTY_RIG,
  EMPTY_SELECTION,
  blocksWithSplits,
  boxSelect,
  buildRig,
  clickBone,
  clickJoint,
  mergePair,
  nodePositions,
  openSuggestions,
  pruneSelection,
  selectedBlocks,
  selectionCentroid,
  selectionJoint,
  type Modifiers,
  type RigSelection,
  type ScreenRect,
  type Vec3,
} from '../../posegraf/rig';
import {
  ZERO_DELTA,
  deltaLabel,
  deltaMatrix,
  isZeroDelta,
  matrixTranslation,
  snapDelta,
  transformRequest,
  type ApplyOptions,
  type GizmoDelta,
} from '../../posegraf/snap';
import { changeSummary, redoTarget, canUndo } from '../../posegraf/versions';
import type { PointCloudScene } from '../../viewport/PointCloudScene';
import { RigScene } from '../../viewport/RigScene';

export type PivotMode = 'centroid' | 'joint' | 'picked';
export type GizmoMode = 'translate' | 'rotate';

/** A short line under the panels: what the last action did, or why it failed. */
export interface EditorNotice {
  tone: 'info' | 'good' | 'warn' | 'crit';
  text: string;
}

function errorText(cause: unknown, what: string): string {
  if (cause instanceof ApiRequestError) {
    if (cause.code === 'nothing_to_undo') return 'Der er intet at fortryde.';
    if (cause.code === 'nothing_to_redo') return 'Der er intet at gentage.';
    if (cause.status === 409) return `${what} blev afvist: et job kører på sagen. Vent, til det er færdigt.`;
    if (cause.status === 403) return 'Du har kun læseadgang til sagen.';
    if (cause.status === 422) return `${what} blev afvist: ${cause.message}`;
  }
  return `${what} mislykkedes. Prøv igen.`;
}

export function usePosegrafEditor({ active, graph, reloadGraph }: { active: boolean; graph: PoseGraph | null; reloadGraph: () => void }) {
  // Viewers see the rig and the history, never the gizmo or a write.
  const canEdit = useCanEdit();
  // ---------------------------------------------------------------- data --
  const blocksRead = useAsync((signal) => (active ? api.poseBlocks(signal) : Promise.resolve(null)), [active]);
  const suggestRead = useAsync<BlockSuggestions | null>((signal) => (active ? api.suggestPoseBlocks({}, signal) : Promise.resolve(null)), [active]);
  const versionsRead = useAsync<PoseVersionList | null>((signal) => (active ? api.poseVersions(signal) : Promise.resolve(null)), [active]);
  const blocks: PoseBlock[] = useMemo(() => blocksRead.data?.blocks ?? [], [blocksRead.data]);
  const suggestions = suggestRead.data ?? null;
  const versions = versionsRead.data ?? null;
  const { reload: reloadBlocks } = blocksRead;
  const { reload: reloadSuggest } = suggestRead;
  const { reload: reloadVersions } = versionsRead;

  const reloadAll = useCallback(() => {
    reloadGraph();
    reloadBlocks();
    reloadSuggest();
    reloadVersions();
  }, [reloadGraph, reloadBlocks, reloadSuggest, reloadVersions]);

  // Another client's (or our own) write.
  const { posegraphChange, jobs } = useJobs();
  const seenChange = useRef(posegraphChange);
  useEffect(() => {
    if (seenChange.current === posegraphChange) return;
    seenChange.current = posegraphChange;
    if (active) reloadAll();
  }, [posegraphChange, active, reloadAll]);
  // A session import announces itself with job.finished, not posegraph.changed.
  const importsDone = jobs.filter((j) => j.stage === 'import_rtabmap' && j.status === 'succeeded').length;
  const seenImports = useRef(importsDone);
  useEffect(() => {
    if (seenImports.current === importsDone) return;
    seenImports.current = importsDone;
    if (active) reloadAll();
  }, [importsDone, active, reloadAll]);

  const rig = useMemo(() => (active ? buildRig(graph, blocks) : EMPTY_RIG), [active, graph, blocks]);
  const positions = useMemo(() => nodePositions(graph), [graph]);

  // ----------------------------------------------------------- selection --
  const [selection, setSelectionRaw] = useState<RigSelection>(EMPTY_SELECTION);
  useEffect(() => setSelectionRaw((s) => pruneSelection(s, blocks)), [blocks]);
  const [ignored, setIgnored] = useState<ReadonlySet<number>>(new Set());
  const open = useMemo(() => openSuggestions(suggestions, blocks, ignored), [suggestions, blocks, ignored]);
  const [focusedSuggestion, setFocusedSuggestion] = useState<number | null>(null);
  const [addSplit, setAddSplit] = useState(false);
  const [boxMode, setBoxMode] = useState(false);
  const [notice, setNotice] = useState<EditorNotice | null>(null);
  const [busy, setBusy] = useState(false);

  // --------------------------------------------------------------- gizmo --
  const [gizmoMode, setGizmoMode] = useState<GizmoMode>('translate');
  const [pivotMode, setPivotMode] = useState<PivotMode>('centroid');
  const [pickedPivot, setPickedPivot] = useState<Vec3 | null>(null);
  const [pickingPivot, setPickingPivot] = useState(false);
  const [snapping, setSnapping] = useState(true);
  const [delta, setDeltaRaw] = useState<GizmoDelta>(ZERO_DELTA);
  const [applyOpts, setApplyOpts] = useState<ApplyOptions>({ mode: 'reseed', blendFrames: 10, hold: false });
  const [preview, setPreview] = useState<BlockTransformResult | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [dragging, setDragging] = useState(false);

  const selBlocks = useMemo(() => selectedBlocks(blocks, selection), [blocks, selection]);
  const centroid = useMemo(() => selectionCentroid(rig, selection), [rig, selection]);
  const joint = useMemo(() => selectionJoint(rig, selection), [rig, selection]);
  const pivot: Vec3 | null =
    selection.size === 0 ? null : pivotMode === 'picked' && pickedPivot ? pickedPivot : pivotMode === 'joint' && joint?.position ? joint.position : centroid;
  const levels = suggestions?.storey_levels ?? [];

  // Selection changes start a fresh move.
  const setSelection = useCallback((next: RigSelection) => {
    setSelectionRaw(next);
    setDeltaRaw(ZERO_DELTA);
    setPreview(null);
  }, []);

  // ---------------------------------------------------------------- scene --
  const [host, setHost] = useState<PointCloudScene | null>(null);
  const rigRef = useRef<RigScene | null>(null);
  const [rigScene, setRigScene] = useState<RigScene | null>(null);
  useEffect(() => {
    if (!host || !active) return;
    const r = new RigScene(host);
    rigRef.current = r;
    setRigScene(r);
    return () => {
      rigRef.current = null;
      setRigScene(null);
      r.dispose();
    };
  }, [host, active]);

  useEffect(() => {
    rigScene?.setRig(rig, open, positions);
  }, [rigScene, rig, open, positions]);
  useEffect(() => {
    rigScene?.setSelection(selection);
  }, [rigScene, selection]);

  // Shift held = fine yaw (1°).
  const shiftRef = useRef(false);
  useEffect(() => {
    const on = (e: KeyboardEvent) => (shiftRef.current = e.shiftKey);
    window.addEventListener('keydown', on);
    window.addEventListener('keyup', on);
    return () => {
      window.removeEventListener('keydown', on);
      window.removeEventListener('keyup', on);
    };
  }, []);

  const snapRef = useRef({ snapping, levels, baseZ: centroid?.[2] ?? 0 });
  snapRef.current = { snapping, levels, baseZ: centroid?.[2] ?? 0 };

  const pivotKey = pivot ? pivot.join(',') : '';
  useEffect(() => {
    if (!rigScene) return;
    if (!pivot || !canEdit) {
      rigScene.attachGizmo(null, null);
      return;
    }
    rigScene.attachGizmo(pivot, {
      start: () => setDragging(true),
      change: (raw) => {
        const s = snapRef.current;
        const snapped = snapDelta(raw, { enabled: s.snapping, fine: shiftRef.current, levels: s.levels, baseZ: s.baseZ });
        rigRef.current?.setGizmoDelta(snapped);
        setDeltaRaw(snapped);
      },
      end: () => setDragging(false),
    });
    setDeltaRaw(ZERO_DELTA);
    setPreview(null);
    // pivotKey stands for the pivot's value.
  }, [rigScene, pivotKey, canEdit]);

  useEffect(() => {
    rigScene?.setGizmoMode(gizmoMode);
  }, [rigScene, gizmoMode, pivotKey]);

  // The live rigid preview follows the delta.
  useEffect(() => {
    if (!rigScene) return;
    rigScene.setPreviewDelta(pivot && !isZeroDelta(delta) ? deltaMatrix(delta, pivot) : null);
  }, [rigScene, delta, pivotKey]);

  /** A typed or reset delta: onto the gizmo too. */
  const setDelta = useCallback((d: GizmoDelta) => {
    setDeltaRaw(d);
    rigRef.current?.setGizmoDelta(d);
    setPreview(null);
  }, []);

  // The server's blended preview, once the drag rests.
  const blockIds = selBlocks.map((b) => b.id);
  const previewKey = JSON.stringify([blockIds, delta, pivot, applyOpts.mode, applyOpts.blendFrames]);
  useEffect(() => {
    if (!rigScene) return;
    if (dragging || !pivot || isZeroDelta(delta) || blockIds.length === 0 || !canEdit) {
      rigScene.setBlendPreview(null);
      return;
    }
    const controller = new AbortController();
    const timer = setTimeout(() => {
      setPreviewing(true);
      api
        .transformPoseBlocks(transformRequest(blockIds, delta, pivot, applyOpts, true), controller.signal)
        .then((result) => {
          setPreview(result);
          // Only the blend zones: the rigid part is already on screen (the bone).
          rigScene.setBlendPreview(
            (result.frames ?? []).filter((f) => f.weight < 0.999).map((f) => ({ position: matrixTranslation(f.after), weight: f.weight })),
          );
        })
        .catch(() => undefined)
        .finally(() => setPreviewing(false));
    }, 350);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [rigScene, previewKey, dragging, canEdit]);

  // -------------------------------------------------------------- writes --
  const write = useCallback(
    async (what: string, run: () => Promise<string | null>) => {
      setBusy(true);
      try {
        const text = await run();
        if (text) setNotice({ tone: 'good', text });
        reloadAll();
      } catch (cause) {
        setNotice({ tone: 'crit', text: errorText(cause, what) });
      } finally {
        setBusy(false);
      }
    },
    [reloadAll],
  );

  const actions = {
    acceptSuggestion: (node: number) => write('Delingen', async () => (await api.splitPoseBlock(node), `Ny blok fra knude ${node}.`)),
    acceptAll: () =>
      write('Delingerne', async () => {
        const nodes = open.map((s) => s.node_id);
        await api.replacePoseBlocks(blocksWithSplits(blocks, nodes, graph?.nodes.map((n) => n.id) ?? []));
        return `${nodes.length} delinger accepteret.`;
      }),
    ignoreSuggestion: (node: number) => setIgnored((s) => new Set(s).add(node)),
    splitAt: (node: number) => write('Delingen', async () => (await api.splitPoseBlock(node), `Ny blok fra knude ${node}.`)),
    merge: () => {
      const pair = mergePair(blocks, selection);
      if (!pair) return;
      void write('Fletningen', async () => (await api.mergePoseBlocks(pair[0].id, pair[1].id), `«${pair[1].name}» flettet ind i «${pair[0].name}».`));
    },
    rename: (block: PoseBlock, name: string) => write('Omdøbningen', async () => (await api.renamePoseBlock(block.id, name), null)),
    detach: (block: PoseBlock, attach: boolean) =>
      write(attach ? 'Genkoblingen' : 'Frakoblingen', async () => {
        const r = await api.detachPoseBlock(block.id, attach);
        if (attach) return `«${block.name}» er koblet til igen (version ${r.version_id}).`;
        if (r.holding_loops === 0) {
          setNotice({ tone: 'warn', text: `«${block.name}» er frakoblet, og ingen løkke holder den på plads — en løsning kan ikke placere den.` });
          return null;
        }
        return `«${block.name}» er frakoblet; ${r.holding_loops} løkker holder den (version ${r.version_id}).`;
      }),
    apply: () => {
      if (!pivot || blockIds.length === 0 || isZeroDelta(delta)) return;
      const label = deltaLabel(selBlocks.map((b) => b.name), delta);
      void write('Flytningen', async () => {
        const r = await api.transformPoseBlocks(transformRequest(blockIds, delta, pivot, applyOpts, false, label));
        setDeltaRaw(ZERO_DELTA);
        setPreview(null);
        rigRef.current?.setBlendPreview(null);
        const warn = r.loops_disagreeing > 0 ? ` ${r.loops_disagreeing} løkker er nu uenige — overvej at afvise dem.` : '';
        return `Version ${r.version_id}: ${r.frames_moved.toLocaleString('da-DK')} billeder flyttet, ${r.frames_blended} blødt overgang.${warn}`;
      });
    },
    reset: () => setDelta(ZERO_DELTA),
    rebuildOdometry: () =>
      write('Odometrien', async () => {
        const r = await api.rebuildPoseOdometry(false);
        return `Odometri genopbygget: ${r.changed} af ${r.pairs.toLocaleString('da-DK')} kanter ændret${r.version_id ? ` (version ${r.version_id})` : ''}.`;
      }),
    undo: () => write('Fortryd', async () => changeSummary(await api.undoPoseVersion())),
    redo: () => write('Gentag', async () => changeSummary(await api.redoPoseVersion())),
    adopt: (id: number) => write('Gendannelsen', async () => changeSummary(await api.adoptPoseVersion(id))),
  };

  // ---------------------------------------------------------------- diff --
  const [diff, setDiff] = useState<PoseVersionDiff | null>(null);
  const compare = useCallback(async (a: number, b: number) => {
    try {
      setDiff(await api.diffPoseVersions(a, b));
    } catch (cause) {
      setNotice({ tone: 'crit', text: errorText(cause, 'Sammenligningen') });
    }
  }, []);

  // ----------------------------------------------------------- pointer --
  /** A canvas click: true when the editor took it. */
  const onCanvasClick = useCallback(
    (x: number, y: number, mods: { shift: boolean; ctrl: boolean }): boolean => {
      const r = rigRef.current;
      if (!active || !host || !r) return false;
      const canvas = host.getCanvas();
      const ndc = new THREE.Vector2((x / canvas.clientWidth) * 2 - 1, -(y / canvas.clientHeight) * 2 + 1);
      const hit = r.pick(host.raycasterAt(ndc));
      if (pickingPivot) {
        let p: Vec3 | null = null;
        if (hit?.kind === 'bone' && hit.node !== null) p = positions.get(hit.node) ?? null;
        else if (hit?.kind === 'joint') p = hit.joint.position;
        else {
          const w = host.pickCloudPoint(ndc);
          if (w) p = [w.x, w.y, w.z];
        }
        if (p) {
          setPickedPivot(p);
          setPivotMode('picked');
          setPickingPivot(false);
        }
        return true;
      }
      const m: Modifiers = { shift: mods.shift, ctrl: mods.ctrl };
      if (addSplit && hit?.kind === 'bone' && hit.node !== null && canEdit) {
        void actions.splitAt(hit.node);
        return true;
      }
      if (hit?.kind === 'joint') {
        setSelection(clickJoint(selection, blocks, hit.joint, m));
        return true;
      }
      if (hit?.kind === 'suggestion') {
        setFocusedSuggestion(hit.node);
        return true;
      }
      if (hit?.kind === 'bone') {
        setSelection(clickBone(selection, hit.key, m));
        return true;
      }
      if (!mods.shift && selection.size > 0) setSelection(EMPTY_SELECTION);
      return false;
    },
    // actions is rebuilt every render; its members only close over state listed here.
    [active, host, pickingPivot, positions, addSplit, canEdit, selection, blocks, setSelection],
  );

  const onBox = useCallback(
    (rect: ScreenRect, mods: Modifiers) => {
      const r = rigRef.current;
      if (!r) return;
      setSelection(boxSelect(selection, r.projectBones(), rect, mods));
    },
    [selection, setSelection],
  );

  // ----------------------------------------------------------- keyboard --
  const keyRef = useRef({ actions, versions, selection });
  keyRef.current = { actions, versions, selection };
  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      const k = keyRef.current;
      const mod = e.ctrlKey || e.metaKey;
      if (mod && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        if (!canEdit) return;
        if (e.shiftKey) {
          if (redoTarget(k.versions)) void k.actions.redo();
        } else if (canUndo(k.versions)) void k.actions.undo();
        return;
      }
      if (mod) return;
      if (e.key === 'Escape') {
        setPickingPivot(false);
        setBoxMode(false);
        setAddSplit(false);
        if (k.selection.size > 0) setSelection(EMPTY_SELECTION);
      } else if (e.key === 'g' || e.key === 'G') setGizmoMode('translate');
      else if (e.key === 'r' || e.key === 'R') setGizmoMode('rotate');
      else if (e.key === 'b' || e.key === 'B') setBoxMode((v) => !v);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, canEdit, setSelection]);

  /** Frame the view on a node (a suggestion row). */
  const focusNode = useCallback(
    (node: number, frame: (min: { x: number; y: number; z: number }, max: { x: number; y: number; z: number }) => void) => {
      setFocusedSuggestion(node);
      const p = positions.get(node);
      if (!p) return;
      const r = 3;
      frame({ x: p[0] - r, y: p[1] - r, z: p[2] - r }, { x: p[0] + r, y: p[1] + r, z: p[2] + r });
    },
    [positions],
  );

  // Dev only: screen positions for the screenshot driver (a real drag needs to know where the gizmo is).
  const debugRef = useRef({ pivot, positions });
  debugRef.current = { pivot, positions };
  useEffect(() => {
    if (!import.meta.env.DEV || !active) return;
    const w = window as unknown as { __posegraf?: unknown };
    w.__posegraf = {
      pivot: () => (debugRef.current.pivot ? (rigRef.current?.screenOf(debugRef.current.pivot) ?? null) : null),
      node: (id: number) => {
        const p = debugRef.current.positions.get(id);
        return p ? (rigRef.current?.screenOf(p) ?? null) : null;
      },
    };
    return () => {
      delete w.__posegraf;
    };
  }, [active]);

  return {
    reloadAll,
    loading: blocksRead.loading && !blocksRead.data,
    error: blocksRead.error ?? null,
    blocks,
    suggestions,
    openSuggestions: open,
    versions,
    rig,
    selection,
    setSelection,
    selBlocks,
    mergeable: mergePair(blocks, selection),
    focusedSuggestion,
    focusNode,
    addSplit,
    setAddSplit,
    boxMode,
    setBoxMode,
    gizmoMode,
    setGizmoMode,
    pivotMode,
    setPivotMode,
    pivot,
    hasJoint: Boolean(joint?.position),
    pickingPivot,
    setPickingPivot,
    snapping,
    setSnapping,
    delta,
    setDelta,
    applyOpts,
    setApplyOpts,
    preview,
    previewing,
    dragging,
    busy,
    notice,
    setNotice,
    diff,
    setDiff,
    compare,
    actions,
    onCanvasClick,
    onBox,
    setHost,
    levels,
  };
}

export type PosegrafEditor = ReturnType<typeof usePosegrafEditor>;
